import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/feedback/ToastHost";
import { useAuth } from "@/hooks/useAuth";
import { errorMessage } from "@/lib/errors";
import * as authApi from "@/services/api/auth";
import { changePasswordSchema, type ChangePasswordValues } from "@/lib/zodSchemas";

/** Current-user profile with session settings and the change-password form. */
export function ProfilePage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [idleTimeout, setIdleTimeout] = useState(
    user?.idle_timeout_minutes?.toString() ?? "30",
  );
  const [savingSession, setSavingSession] = useState(false);

  const saveSession = async () => {
    setSavingSession(true);
    try {
      const minutes = Math.min(Math.max(Number(idleTimeout) || 30, 1), 120);
      const updated = await authApi.updateProfile({ idle_timeout_minutes: minutes });
      setUser(updated);
      setIdleTimeout(minutes.toString());
      toast.success(t("admin.saved"));
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSavingSession(false);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await authApi.changePassword(values.current_password, values.new_password);
      toast.success(t("auth.passwordChanged"));
      reset();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("auth.profile")}</CardTitle>
          <CardDescription>{user?.email}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          <p>
            <span className="text-muted-foreground">Name: </span>
            {user?.full_name}
          </p>
          <p>
            <span className="text-muted-foreground">Locale: </span>
            {user?.preferred_locale}
          </p>
          <p>
            <span className="text-muted-foreground">Session: </span>
            {user?.session_profile}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("auth.sessionSettings")}</CardTitle>
          <CardDescription>{t("auth.idleTimeoutHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex max-w-sm items-end gap-2">
            <div className="space-y-2">
              <Label htmlFor="idle_timeout">{t("auth.idleTimeoutMinutes")}</Label>
              <Input
                id="idle_timeout"
                type="number"
                min={1}
                max={120}
                value={idleTimeout}
                onChange={(event) => setIdleTimeout(event.target.value)}
              />
            </div>
            <Button onClick={() => void saveSession()} disabled={savingSession}>
              {t("common.save")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("auth.changePassword")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">{t("auth.currentPassword")}</Label>
              <Input id="current_password" type="password" {...register("current_password")} />
              {errors.current_password ? (
                <p className="text-sm text-destructive">{errors.current_password.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password">{t("auth.newPassword")}</Label>
              <Input id="new_password" type="password" {...register("new_password")} />
              {errors.new_password ? (
                <p className="text-sm text-destructive">{errors.new_password.message}</p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password">{t("auth.confirmPassword")}</Label>
              <Input id="confirm_password" type="password" {...register("confirm_password")} />
              {errors.confirm_password ? (
                <p className="text-sm text-destructive">{errors.confirm_password.message}</p>
              ) : null}
            </div>
            <Button type="submit" disabled={submitting}>
              {t("common.save")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
